import { useEffect, useState } from 'react';
import { useAppState } from '@afosto/app-components-react/AppProvider';
import useSubscriptions from '@afosto/utils-react/subscription/useSubscriptions';
import { fulfilment as fulfilmentApi } from '@afosto/fulfilment/api/Fulfilment';
import { inventory as inventoryApi } from '@afosto/wms/api/Inventory';
import { locations as locationsApi } from '@afosto/wms/api/Locations';

const usePositionSuggestions = ({ locationId, sku, position: itemPosition }) => {
  const subscriptions = useSubscriptions();
  const hasWmsSubscription = subscriptions.some(({ code: appCode }) => appCode === 'wms');
  const { settings = {} } = useAppState();
  const showPositionSuggestions = settings?.general?.show_position_suggestions ?? true;
  const [positionSuggestions, setPositionSuggestions] = useState([]);

  useEffect(() => {
    const fetchWmsLocation = async id => {
      try {
        const response = await fulfilmentApi.getFulfilmentLocation(id);
        const { data } = response;

        if (data?.address_id) {
          const wmsLocationResponse = await locationsApi
            .searchALocation({
              address_id: data.address_id,
            })
            .catch(() => null);

          return wmsLocationResponse?.data ?? null;
        }

        return null;
      } catch (err) {
        return null;
      }
    };

    const fetchPositionSuggestions = async () => {
      try {
        const wmsLocation = await fetchWmsLocation(locationId);
        const response = await fulfilmentApi.searchPositions(locationId, [sku]);
        const { data = [] } = response;

        let positions = data
          .filter(({ position }) => position !== itemPosition)
          .map(({ position }) => position);

        if (hasWmsSubscription && wmsLocation?.id) {
          const inventoryResponse = await inventoryApi
            .searchInventorySummary({
              constraints: [
                {
                  key: 'location_id',
                  operator: 'in',
                  values: [wmsLocation.id],
                },
                {
                  key: 'sku',
                  operator: 'in',
                  values: [sku],
                },
              ],
            })
            .catch(() => {});
          const availableWmsPositions = (inventoryResponse?.data ?? [])
            .map(inventory => inventory?.position)
            .filter(
              wmsPosition =>
                wmsPosition && !positions.includes(wmsPosition) && wmsPosition !== itemPosition,
            );

          positions = [...positions, ...availableWmsPositions];
        }

        setPositionSuggestions(positions.slice(0, 5));
      } catch (err) {
        setPositionSuggestions([]);
      }
    };

    if (showPositionSuggestions && locationId && sku) {
      fetchPositionSuggestions();
    }
  }, [showPositionSuggestions, locationId, sku, itemPosition, hasWmsSubscription]);

  return positionSuggestions;
};

export default usePositionSuggestions;
